/**
 * Page `$input` contract resolution (ADR-0045 R4-inputs).
 *
 * A page that is embedded (`page_ref`) or opened as an overlay (`open_overlay`)
 * receives a read-only `$input` map from its host. The HOST needs to know WHICH
 * parameters the target page expects so it can render a guided form instead of
 * an untyped free-form key→value map. Two sources, in priority order (the
 * "hybrid" model):
 *
 *  1. **Declared** — `ast.inputs` (the `PageInput[]` contract the page author
 *     declared in the Stato/Ingressi editor): carries name + type + required +
 *     description. This is the robust, self-documenting path.
 *  2. **Discovered** — when a page declares nothing, scan its AST for every
 *     `$input.<key>` read in a `{{ }}` binding and surface those keys as
 *     untyped, optional inputs. Zero authoring burden; the host still gets a
 *     guided form.
 *
 * Pure + total (no React, no eval) so it's unit-testable and cheap to run over
 * every page the builder already holds in memory.
 */
import type { PageAst, PageInput, StateValueType } from "./pageTypes";

/** A target page's resolved input — what the host editor renders one row for.
 *  `declared` distinguishes an author-declared contract entry from an
 *  auto-discovered `$input.<key>` usage (the UI labels them differently). */
export interface ResolvedPageInput {
  name: string;
  type?: StateValueType;
  required: boolean;
  description?: string;
  declared: boolean;
}

/** Identifier after `$input.` — the key it addresses. */
const INPUT_REF_RE = /\$input\s*\.\s*([A-Za-z_$][\w$]*)/g;

/**
 * Deep-scan an arbitrary value (typically a whole page AST) for the `$input.<key>`
 * keys it READS inside `{{ }}` bindings — wherever a binding sits (a node prop,
 * a visible_when, a query param, an action arg, a JS Object body). Returns the
 * distinct keys in first-seen order. Pure + total; a non-object/array is a no-op.
 */
export function discoverInputKeys(value: unknown, out: string[] = []): string[] {
  if (typeof value === "string") {
    if (value.includes("$input")) {
      for (const m of value.matchAll(INPUT_REF_RE)) {
        const k = m[1];
        if (k && !out.includes(k)) out.push(k);
      }
    }
  } else if (Array.isArray(value)) {
    for (const v of value) discoverInputKeys(v, out);
  } else if (value && typeof value === "object") {
    for (const v of Object.values(value as Record<string, unknown>)) discoverInputKeys(v, out);
  }
  return out;
}

/**
 * Resolve the inputs a target page expects: its DECLARED contract if any, else
 * the keys auto-DISCOVERED from its `$input.*` usages. Returns `[]` when the
 * page neither declares nor uses any input (the host then falls back to a plain
 * free-form editor).
 */
export function resolvePageInputs(ast: PageAst | undefined | null): ResolvedPageInput[] {
  const declared = ast?.inputs ?? [];
  if (declared.length > 0) {
    return declared.map((p: PageInput) => ({
      name: p.name,
      type: p.type,
      required: Boolean(p.required),
      description: p.description,
      declared: true,
    }));
  }
  return discoverInputKeys(ast).map((name) => ({ name, required: false, declared: false }));
}
